import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  MarkCaution,
  MarkInTolerance,
  MarkOutOfTolerance,
  Panel,
  fieldGridClass,
} from '../../components/ui/index.js';
import { ResultGrid, isolateNotation } from './specialized-form/index.js';

/**
 * THE BENCH — the layout of test execution, review and approval (D278).
 *
 * PRESENTATION ONLY. Nothing in this file knows a field, a unit, a formula or a
 * rule of any test: each specialised form passes in what it already shows, and
 * these parts only arrange it — sections jumped to from a strip, a main column
 * and a side column, one card per determination.
 *
 * Entered and calculated values stay visibly different (CLAUDE.md section 8):
 * a card's entered values sit on the plain sheet, its calculated values on the
 * `bg-calc` tint below a dashed rule, each drawn by `Reading` with its own mark.
 */

export type SectionState = 'done' | 'todo' | 'none';

export interface BenchStep {
  readonly id: string;
  readonly label: string;
  readonly state: SectionState;
}

/** A section a step jumps to. The scroll margin keeps it clear of the top bar. */
export function BenchSection({ id, children }: { readonly id: string; readonly children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-20" tabIndex={-1}>
      {children}
    </div>
  );
}

function StateMark({ state }: { readonly state: SectionState }) {
  const { t } = useTranslation();
  if (state === 'done') {
    return (
      <span className="inline-flex text-success-700">
        <MarkInTolerance size={13} />
        <span className="sr-only">{t('bench.stateDone')}</span>
      </span>
    );
  }
  if (state === 'todo') {
    return (
      <span className="inline-flex text-warning-700">
        <MarkCaution size={13} />
        <span className="sr-only">{t('bench.stateTodo')}</span>
      </span>
    );
  }
  return null;
}

/** The horizontal strip of sections, each with its done / to-do mark. */
export function SectionStrip({ steps }: { readonly steps: readonly BenchStep[] }) {
  const { t } = useTranslation();
  const jump = (id: string) => {
    const target = globalThis.document?.getElementById(id);
    if (!target) return;
    target.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  };
  return (
    <nav
      aria-label={t('bench.sections')}
      className="flex max-w-full flex-wrap gap-1 rounded-large border border-default-200 bg-white p-1"
      data-testid="bench-steps"
    >
      {steps.map((step) => (
        <button
          key={step.id}
          type="button"
          onClick={() => jump(step.id)}
          className="inline-flex items-center gap-1.5 rounded-medium px-3 py-1.5 text-body font-medium text-default-700 hover:bg-default-100 hover:text-default-900 focus-visible:outline-2 focus-visible:outline-primary"
        >
          <StateMark state={step.state} />
          {step.label}
        </button>
      ))}
    </nav>
  );
}

/**
 * Two columns on a wide screen: the work, and a narrow side column that stays
 * in view while the work scrolls. One column, the side column last, on a phone.
 */
export function BenchLayout({
  strip,
  main,
  side,
}: {
  readonly strip?: ReactNode;
  readonly main: ReactNode;
  readonly side: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-stack">
      {strip}
      <div className="grid grid-cols-1 items-start gap-gutter lg:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="flex min-w-0 flex-col gap-gutter">{main}</div>
        <aside className="flex min-w-0 flex-col gap-gutter lg:sticky lg:top-4">{side}</aside>
      </div>
    </div>
  );
}

export type CardState = 'calculated' | 'incomplete' | 'check';

/** The state written in a determination card's header, in words and a mark. */
export function CardStateLabel({ state, label }: { readonly state: CardState; readonly label: string }) {
  const tone =
    state === 'calculated' ? 'text-success-700' : state === 'check' ? 'text-warning-700' : 'text-default-600';
  return (
    <span className={`inline-flex items-center gap-1 text-caption font-medium ${tone}`} data-card-state={state}>
      {state === 'calculated' ? <MarkInTolerance size={12} /> : state === 'check' ? <MarkCaution size={12} /> : null}
      {label}
    </span>
  );
}

/**
 * One determination, specimen or fraction set — instead of a wide table that
 * scrolls sideways. Entered fields sit in an ALIGNED grid (D328): one row of
 * equal columns when the card is wide enough, every input on one line however
 * long its label; two columns, then one, as the card narrows. Calculated
 * values sit below on the calculated tint, in equal cells; notes and problems
 * close the card.
 */
export function DeterminationCard({
  title,
  state,
  onRemove,
  removeLabel,
  entered,
  calculated,
  footer,
  testId,
  calculatedTestId,
  calculatedSource,
  ariaLabel,
}: {
  readonly title: string;
  readonly state?: ReactNode;
  readonly onRemove?: () => void;
  readonly removeLabel?: string;
  readonly entered: ReactNode;
  readonly calculated?: ReactNode;
  readonly footer?: ReactNode;
  readonly testId: string;
  readonly calculatedTestId?: string;
  readonly calculatedSource?: string;
  readonly ariaLabel: string;
}) {
  return (
    <li className="min-w-0 rounded-large border border-default-200 bg-white" data-testid={testId} aria-label={ariaLabel}>
      <div className="flex flex-wrap items-center gap-x-stack gap-y-1 rounded-t-large border-b border-default-200 bg-default-50 px-3 py-2">
        <p className="text-body font-semibold text-default-900">{title}</p>
        <span className="flex-1" />
        {state}
        {onRemove && removeLabel ? (
          <ActionButton priority="tertiary" size="sm" onPress={onRemove}>
            {removeLabel}
          </ActionButton>
        ) : null}
      </div>
      <div className="@container p-3">
        <div className={fieldGridClass('row')} data-testid="determination-fields">
          {entered}
        </div>
      </div>
      {calculated ? (
        <ResultGrid
          surface="card"
          {...(footer ? {} : { className: 'rounded-b-large' })}
          {...(calculatedTestId ? { testId: calculatedTestId } : {})}
          {...(calculatedSource ? { source: calculatedSource } : {})}
        >
          {calculated}
        </ResultGrid>
      ) : null}
      {footer ? <div className="px-3 py-2">{footer}</div> : null}
    </li>
  );
}

/** One entered value, read-only: its label, then the value as typed. */
export function EnteredItem({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-caption text-default-600">{label}</span>
      <span dir="ltr" className="text-start">
        {children}
      </span>
    </div>
  );
}

export interface LiveValue {
  readonly key: string;
  readonly label: string;
  readonly value: ReactNode;
}

/** The side column's live results: what the system calculated, never typed. */
export function LiveResultsPanel({ values, emptyLabel }: { readonly values: readonly LiveValue[]; readonly emptyLabel: string }) {
  const { t } = useTranslation();
  return (
    <Panel title={t('bench.liveResults')} description={t('bench.liveResultsHint')}>
      {values.length === 0 ? (
        <p className="text-body text-default-600">{emptyLabel}</p>
      ) : (
        <dl className="flex flex-col divide-y divide-default-100 rounded-medium bg-calc px-3">
          {values.map((value) => (
            <div key={value.key} className="flex flex-col gap-0.5 py-2">
              <dt className="text-caption text-calc-ink">{isolateNotation(value.label)}</dt>
              <dd dir="ltr" className="text-start">
                {value.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </Panel>
  );
}

export interface CheckItem {
  readonly key: string;
  readonly ok: boolean;
  readonly label: string;
}

/** A list of checks, each with a drawn mark and words — never colour alone. */
export function CheckList({ items }: { readonly items: readonly CheckItem[] }) {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-col gap-2" data-testid="bench-checks">
      {items.map((item) => (
        <li key={item.key} className="flex items-start gap-2 text-body text-default-800">
          <span className={`mt-0.5 inline-flex ${item.ok ? 'text-success-700' : 'text-warning-700'}`}>
            {item.ok ? <MarkInTolerance size={14} /> : <MarkOutOfTolerance size={14} />}
            <span className="sr-only">{item.ok ? t('bench.stateDone') : t('bench.stateTodo')}</span>
          </span>
          <span className="min-w-0">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * "Before you submit": a guide built from what is on screen, with Save draft
 * (secondary) and Submit for review (primary) beneath it. The list never gates
 * anything — the server checks the record again on every save and submission,
 * and its answer is the one shown.
 */
export function BeforeSubmitPanel({
  checks,
  actions,
  summary,
}: {
  readonly checks: readonly CheckItem[];
  readonly actions?: ReactNode;
  readonly summary?: ReactNode;
}) {
  const { t } = useTranslation();
  const open = checks.filter((check) => !check.ok).length;
  return (
    <Panel
      title={open === 0 ? t('bench.readyToSubmit') : t('bench.beforeSubmit', { count: open })}
      description={t('bench.checkNote')}
    >
      <div className="flex flex-col gap-stack">
        <CheckList items={checks} />
        {actions ? (
          <div className="flex flex-wrap items-center gap-inline border-t border-default-200 pt-stack">{actions}</div>
        ) : null}
        {summary}
      </div>
    </Panel>
  );
}

/** A small titled group inside a panel or a frozen record. */
export function SubSection({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h3 className="text-caption font-semibold text-default-700">{title}</h3>
      {children}
    </section>
  );
}
